import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Crown, Eye, TrendingUp, TrendingDown, ArrowRight, Sparkles, Share2 } from "lucide-react";
import FounderShareCard from "./FounderShareCard";

/**
 * FounderWallStatsCard — Wallet-page tile showing Elder Founder analytics.
 * Hidden entirely for non-Elder providers and Clients.
 *
 * - Lifetime profile views
 * - Week-over-week trend (% lift)
 * - "You were Founder of the Week on YYYY-Www" callout with click count
 * - Quick link to the public Elder profile
 */
export default function FounderWallStatsCard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/providers/me/founder-wall-stats");
        setStats(data);
      } catch { /* hide silently */ }
      setLoading(false);
    })();
  }, []);

  if (loading || !stats?.isElder) return null;
  const lift = stats.liftPct;
  const liftUp = lift != null && lift >= 0;
  const LiftIcon = liftUp ? TrendingUp : TrendingDown;

  return (
    <div className="parchment p-6 border-secondary/40 bg-gradient-to-br from-secondary/[0.06] to-transparent space-y-5"
         data-testid="founder-wall-stats-card">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="inline-flex items-center gap-2 text-secondary">
          <Crown size={13} strokeWidth={2} />
          <span className="label-mystic">Founder Wall · Your Stats</span>
        </div>
        {stats.elderIndex && (
          <span className="text-[11px] uppercase tracking-[0.22em] text-foreground/55">
            #{String(stats.elderIndex).padStart(4, "0")} of 500
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Stat
          icon={Eye}
          label="Lifetime views"
          value={stats.lifetimeViews}
          testId="founder-stats-lifetime"
        />
        <Stat
          icon={LiftIcon}
          label="This week"
          value={stats.viewsThisWeek}
          accent={lift != null}
          accentColor={liftUp ? "text-secondary" : "text-destructive"}
          subtext={
            lift != null
              ? `${lift >= 0 ? "+" : ""}${lift}% vs. last week`
              : "First week with traffic"
          }
          testId="founder-stats-week"
        />
        <Stat
          icon={Sparkles}
          label="Last week"
          value={stats.viewsLastWeek}
          subtext={`${stats.viewsLastWeek === 1 ? "view" : "views"}`}
          testId="founder-stats-last-week"
        />
      </div>

      {stats.lastFeaturedWeek ? (
        <div className="border-l-2 border-secondary/50 pl-4 py-1" data-testid="founder-stats-featured-callout">
          <div className="label-mystic text-secondary inline-flex items-center gap-2">
            <Crown size={10} strokeWidth={2.25} fill="currentColor" /> Founder of the Week
          </div>
          <div className="font-serif text-lg mt-1">
            You were featured on <span className="text-secondary">{stats.lastFeaturedWeek}</span>
          </div>
          <div className="text-xs text-foreground/65 mt-1">
            {stats.lastFeaturedClicks} click{stats.lastFeaturedClicks === 1 ? "" : "s"} on your Founder card during that week.
          </div>
        </div>
      ) : (
        <div className="text-xs text-foreground/55 italic" data-testid="founder-stats-not-featured-yet">
          You haven't been featured as Founder of the Week in the last 12 weeks. The rotation re-shuffles every Monday at 14 UTC.
        </div>
      )}

      {/* Share-card clickback attribution */}
      {(stats.shareCardLifetime || 0) > 0 || (stats.shareCardThisWeek || 0) > 0 ? (
        <div className="border-l-2 border-primary/50 pl-4 py-1" data-testid="founder-stats-sharecard-callout">
          <div className="label-mystic text-primary inline-flex items-center gap-2">
            <Share2 size={10} strokeWidth={2} /> Share-card clickbacks
          </div>
          <div className="font-serif text-lg mt-1">
            <span className="text-primary" data-testid="founder-stats-sharecard-this-week">
              {stats.shareCardThisWeek || 0}
            </span>
            <span className="text-foreground/60 text-sm ml-2">clicks this week</span>
          </div>
          <div className="text-xs text-foreground/65 mt-1">
            {stats.shareCardLifetime} lifetime clickback{stats.shareCardLifetime === 1 ? "" : "s"} from your downloaded spotlight cards.
          </div>
        </div>
      ) : null}

      <div className="pt-1 flex items-center justify-between gap-3 flex-wrap">
        <Link
          to={stats.publicProfileUrl}
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-secondary hover:text-secondary/80 group"
          data-testid="founder-stats-public-link"
        >
          View public profile
          <ArrowRight size={11} className="group-hover:translate-x-0.5 transition-transform" />
        </Link>
        <FounderShareCard
          elder={{
            userId: stats.userId,
            name: stats.name,
            businessName: stats.businessName,
            elderIndex: stats.elderIndex,
            tradition: stats.tradition,
            bio: stats.bio,
            lineageStatement: stats.lineageStatement,
            yearsInitiated: stats.yearsInitiated,
            category: stats.category,
            avatarUrl: stats.avatarUrl,
          }}
          stats={{
            lifetimeViews: stats.lifetimeViews,
            liftPct: stats.liftPct,
          }}
        />
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, subtext, accent, accentColor, testId }) {
  return (
    <div data-testid={testId}>
      <div className="label-mystic text-foreground/55 inline-flex items-center gap-1.5">
        <Icon size={11} strokeWidth={1.5} /> {label}
      </div>
      <div className={`font-serif text-3xl mt-1 ${accent ? accentColor : "text-foreground/90"}`}>
        {value}
      </div>
      {subtext && <div className="text-[11px] text-foreground/55 mt-0.5">{subtext}</div>}
    </div>
  );
}
